/**
 * AI CONTEXT
 * Purpose: maps app theme tokens into Ant Design component/theme overrides.
 * Configures: global token colors plus Button/Menu/Radio component theme knobs.
 * Used by: App provider stack for all Antd rendering.
 * Importance: keeps Antd visuals aligned with custom theme palette.
 * Refactor: remove unused local color vars and move token docs to design notes.
 */
import { ConfigProvider } from "antd"

import { type ReactChild } from "#customTypes/globalTypes"

import { useThemeContext } from "./ThemeContextProvider"

/**
 *
 * Some Antd notes
 *
 * Active means when you click
 *
 * Antd calls the header "Submenu" and the dropdown "Menu"
 * Color means text for default and background for primary (buttons so far)
 * Bg means background
 * There is often an active bg but not an active color
 *
 */
// Active means when you click
const AntdRethemeContextProvider = ({ children }: { children: ReactChild }) => {
  const theme = useThemeContext()
  const { backgroundColor, highlightColor, mainColor, white, black, gray } =
    theme

  return (
    <ConfigProvider
      theme={{
        token: {
          // Main primary bg color (buttons, etc) - overrides everything
          // Also background for things like inputs for some reason
          colorBgContainer: white,

          // Button color
          colorPrimary: mainColor, // Main button color
          colorPrimaryHover: highlightColor, // Button border
          colorPrimaryActive: backgroundColor, // Button text on default click and also bg on primary click
        },
        components: {
          Button: {
            // colorText: white,
            // colorBorder: backgroundColor,
            defaultActiveBg: mainColor,
            defaultHoverColor: gray[8], // Button text color on hover
            defaultHoverBg: highlightColor, // Button bg on hover
          },

          // Antd calls the header "Submenu" and the dropdown "Menu"
          // <Header> also has some manual overrides locally
          Menu: {
            // Text colors
            colorPrimary: white, // Main selected text color
            itemColor: black, // Main text color for whole menu

            horizontalItemSelectedColor: white, // Header selected underline color
            subMenuItemSelectedColor: white, // Header selected text color (dupe?)

            itemSelectedColor: white, // Dropdown selected text color
            itemHoverColor: mainColor, // Dropdown hover text color

            // Background colors
            colorBgContainer: mainColor, // Header background color
            // horizontalItemSelectedBg: activeColor, // Header selected background
            itemSelectedBg: mainColor, // Dropdown selected background
            itemActiveBg: "#00000011", // Dropdown background on click
            itemHoverBg: "transparent", // Dropdown background on hover

            // Submenu-specific tokens (for dropdown)
            popupBg: gray[1], // Submenu background color
          },
          Radio: {
            // colorBorder: backgroundColor,
            // colorText: white,
            colorTextDisabled: "#cccccc",
            buttonBg: white, // unchecked radio button bg
            buttonCheckedBg: "pink", // checked radio button bg
            buttonSolidCheckedActiveBg: backgroundColor, // checked radio button bg on click
            // buttonSolidCheckedHoverBg: "pink", // Hover active radio button bg
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

export default AntdRethemeContextProvider
